import { useState } from 'react';
import { Plus, Pencil, Trash2, GripVertical, Eye, EyeOff, KeyRound, Link2, Cpu, Loader2, Wifi } from 'lucide-react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfigStore } from '@/store/useConfigStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal, ConfirmDialog } from '@/components/ui/Modal';
import { Tag, TagInput } from '@/components/ui/Tag';
import { generateId } from '@/lib/storage';
import { cn } from '@/lib/utils';
import type { LLMInstance } from '@/types';
import { callLLM, fetchLLMModels } from '@/services/llmService';

interface FormState {
    name: string;
    apiUrl: string;
    apiKey: string;
    models: string[];
    protocol: 'openai-compatible' | 'sub2api';
}

const emptyForm: FormState = { name: '', apiUrl: '', apiKey: '', models: [], protocol: 'openai-compatible' };

export function LLMConfigPage({ embedded = false }: { embedded?: boolean } = {}) {
    const {
        llmInstances,
        addLLMInstance,
        updateLLMInstance,
        deleteLLMInstance,
        reorderLLMInstances,
    } = useConfigStore();

    const [formOpen, setFormOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<FormState>(emptyForm);
    const [deleteId, setDeleteId] = useState<string | null>(null);

    const openAdd = () => {
        setForm(emptyForm);
        setEditingId(null);
        setFormOpen(true);
    };

    const openEdit = (inst: LLMInstance) => {
        setForm({
            name: typeof inst.name === 'string' ? inst.name : '',
            apiUrl: typeof inst.apiUrl === 'string' ? inst.apiUrl : '',
            apiKey: typeof inst.apiKey === 'string' ? inst.apiKey : '',
            models: Array.isArray(inst.models) ? inst.models.filter((model): model is string => typeof model === 'string') : [],
            protocol: inst.protocol ?? 'openai-compatible',
        });
        setEditingId(inst.id);
        setFormOpen(true);
    };

    const submit = () => {
        const name = form.name.trim();
        const apiUrl = form.apiUrl.trim();
        if (!name || !apiUrl) return;
        const data = {
            name,
            apiUrl,
            apiKey: form.apiKey.trim(),
            models: form.models.map(m => m.trim()).filter(Boolean),
            protocol: form.protocol,
        };
        if (editingId) {
            updateLLMInstance(editingId, data);
        } else {
            addLLMInstance({ id: generateId(), ...data });
        }
        setFormOpen(false);
    };

    const confirmDelete = () => {
        if (deleteId) deleteLLMInstance(deleteId);
        setDeleteId(null);
    };

    const handleDragEnd = (e: DragEndEvent) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        const oldIndex = llmInstances.findIndex(i => i.id === active.id);
        const newIndex = llmInstances.findIndex(i => i.id === over.id);
        if (oldIndex < 0 || newIndex < 0) return;
        reorderLLMInstances(arrayMove(llmInstances, oldIndex, newIndex));
    };

    return (
        <div className="flex h-full flex-col">
            {!embedded && <div className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-4 py-3">
                <div>
                    <h1 className="text-base font-semibold text-zinc-900">LLM 实例管理</h1>
                    <p className="text-xs text-zinc-500">管理大语言模型接口实例，拖拽卡片可调整顺序</p>
                </div>
                <Button variant="primary" size="sm" onClick={openAdd}>
                    <Plus size={14} />
                    添加实例
                </Button>
            </div>}
            {embedded && <div className="flex shrink-0 justify-end px-4 pt-3"><Button variant="primary" size="sm" onClick={openAdd}><Plus size={14} />添加实例</Button></div>}

            <div className="flex-1 overflow-auto p-4">
                {llmInstances.length === 0 ? (
                    <EmptyState onAdd={openAdd} />
                ) : (
                    <div className="mx-auto max-w-3xl">
                        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext
                                items={llmInstances.map(i => i.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="flex flex-col gap-3">
                                    {llmInstances.map(inst => (
                                        <SortableCard
                                            key={inst.id}
                                            instance={inst}
                                            onEdit={() => openEdit(inst)}
                                            onDelete={() => setDeleteId(inst.id)}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    </div>
                )}
            </div>

            <Modal
                open={formOpen}
                onClose={() => setFormOpen(false)}
                title={editingId ? '编辑实例' : '添加实例'}
                width="md"
            >
                <LLMInstanceForm
                    form={form}
                    onChange={setForm}
                    onSubmit={submit}
                    onCancel={() => setFormOpen(false)}
                />
            </Modal>

            <ConfirmDialog
                open={!!deleteId}
                title="删除实例"
                message="确定要删除该 LLM 实例吗？此操作不可撤销。"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteId(null)}
            />
        </div>
    );
}

function SortableCard({
    instance,
    onEdit,
    onDelete,
}: {
    instance: LLMInstance;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: instance.id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };
    const apiKey = typeof instance.apiKey === 'string' ? instance.apiKey : '';
    const models = Array.isArray(instance.models) ? instance.models : [];
    const maskedKey = apiKey
        ? apiKey.length > 4
            ? `••••••••${apiKey.slice(-4)}`
            : '••••••••'
        : '未设置';
    const [testing, setTesting] = useState(false);
    const [testMessage, setTestMessage] = useState('');

    const testConnection = async () => {
        const model = models[0];
        if (!model || !apiKey.trim()) return;
        setTesting(true);
        setTestMessage('');
        try {
            await callLLM(instance, model, '你正在执行接口连通测试。', '只返回 OK');
            setTestMessage('连接成功');
        } catch (error) {
            setTestMessage(error instanceof Error ? error.message : '连接失败');
        } finally { setTesting(false); }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={cn(
                'flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-4 shadow-sm',
                isDragging && 'opacity-70 shadow-md ring-2 ring-zinc-300',
            )}
        >
            <button
                {...attributes}
                {...listeners}
                className="mt-0.5 cursor-grab touch-none rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 active:cursor-grabbing"
                title="拖拽排序"
            >
                <GripVertical size={18} />
            </button>

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><h3 className="truncate text-sm font-semibold text-zinc-900">{instance.name}</h3><span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-500">{instance.protocol === 'sub2api' ? 'Sub2API' : 'OpenAI Compatible'}</span></div>
                <div className="mt-2 flex flex-col gap-1.5 text-xs text-zinc-500">
                    <div className="flex items-center gap-1.5">
                        <Link2 size={13} className="shrink-0 text-zinc-400" />
                        <span className="truncate" title={instance.apiUrl}>{instance.apiUrl}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <KeyRound size={13} className="shrink-0 text-zinc-400" />
                        <span className="font-mono">{maskedKey}</span>
                    </div>
                </div>
                {models.length > 0 && (
                    <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                        <Cpu size={13} className="text-zinc-400" />
                        {models.map((m, i) => (
                            <Tag key={`${m}-${i}`} label={m} color="blue" />
                        ))}
                    </div>
                )}
                {testMessage && <p className={`mt-2 text-xs ${testMessage === '连接成功' ? 'text-emerald-600' : 'text-red-600'}`}>{testMessage}</p>}
            </div>

            <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => void testConnection()} disabled={testing || !apiKey.trim() || !models[0]}>
                    {testing ? <Loader2 size={14} className="animate-spin" /> : <Wifi size={14} />}测试
                </Button>
                <Button variant="ghost" size="sm" onClick={onEdit}>
                    <Pencil size={14} />
                    编辑
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onDelete}
                    className="text-red-500 hover:bg-red-50 hover:text-red-600"
                >
                    <Trash2 size={14} />
                    删除
                </Button>
            </div>
        </div>
    );
}

function LLMInstanceForm({
    form,
    onChange,
    onSubmit,
    onCancel,
}: {
    form: FormState;
    onChange: (f: FormState) => void;
    onSubmit: () => void;
    onCancel: () => void;
}) {
    const [showKey, setShowKey] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);
    const [modelMessage, setModelMessage] = useState('');
    const update = (patch: Partial<FormState>) => onChange({ ...form, ...patch });
    const valid = form.name.trim() !== '' && form.apiUrl.trim() !== '';

    return (
        <div className="flex flex-col gap-4">
            <Input
                label="名称"
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                placeholder="如：DeepSeek"
            />
            <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-zinc-600">协议</label>
                <select value={form.protocol} onChange={(event) => update({ protocol: event.target.value as FormState['protocol'] })} className="h-9 rounded-md border border-zinc-300 bg-white px-3 text-sm">
                    <option value="openai-compatible">OpenAI Compatible</option>
                    <option value="sub2api">Sub2API（OpenAI Compatible）</option>
                </select>
            </div>
            <Input
                label="API Base URL"
                value={form.apiUrl}
                onChange={(e) => update({ apiUrl: e.target.value })}
                placeholder="https://api.deepseek.com"
            />
            <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-zinc-600">API Key</label>
                <div className="relative">
                    <input
                        type={showKey ? 'text' : 'password'}
                        value={form.apiKey}
                        onChange={(e) => update({ apiKey: e.target.value })}
                        placeholder="sk-..."
                        className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 pr-9 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-500 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                    />
                    <button
                        type="button"
                        onClick={() => setShowKey(v => !v)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                        title={showKey ? '隐藏' : '显示'}
                    >
                        {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                </div>
            </div>
            <div>
                <div className="mb-1 flex items-center justify-between"><span className="text-xs font-medium text-zinc-600">模型列表</span><Button variant="outline" size="sm" disabled={loadingModels || !form.apiUrl.trim() || !form.apiKey.trim()} onClick={async () => {
                    setLoadingModels(true); setModelMessage('');
                    try { const models = await fetchLLMModels({ id: 'preview', ...form }); update({ models }); setModelMessage(`已读取 ${models.length} 个模型`); }
                    catch (error) { setModelMessage(error instanceof Error ? error.message : '读取模型失败'); }
                    finally { setLoadingModels(false); }
                }}>{loadingModels ? <Loader2 size={13} className="animate-spin" /> : <Cpu size={13} />}读取模型</Button></div>
                <TagInput tags={form.models} onAdd={(tag) => update({ models: [...form.models, tag] })} onRemove={(i) => update({ models: form.models.filter((_, idx) => idx !== i) })} placeholder="输入模型名称后回车添加" />
                {modelMessage && <p className="mt-1 text-xs text-zinc-500">{modelMessage}</p>}
            </div>
            <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={onCancel}>取消</Button>
                <Button variant="primary" size="sm" onClick={onSubmit} disabled={!valid}>
                    保存
                </Button>
            </div>
        </div>
    );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
    return (
        <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-zinc-100 text-zinc-400">
                <Cpu size={28} />
            </div>
            <div>
                <p className="text-sm font-medium text-zinc-700">暂无 LLM 实例</p>
                <p className="text-xs text-zinc-400">添加一个实例以开始使用标题优化等功能</p>
            </div>
            <Button variant="primary" size="sm" onClick={onAdd}>
                <Plus size={14} />
                添加实例
            </Button>
        </div>
    );
}
